import React, { useEffect, useState } from "react";
import { withAuthenticationRequired } from "../context/AuthContext";
import Loading from "../components/Loading";
import { Button, Tooltip } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useRequestHandler } from "../utils/useRequestHandler";
import Servicios from "../services";
import { HallazgosValidacionList } from "../components/HallazgosValidacionList";
import { TransicionEstadoDialog } from "../components/TransicionEstadoDialog";

/**
 * Pantalla de transición de estado de un Concurso. Reemplazo completo de la
 * versión anterior, que nunca cargaba el concurso (título fijo "Nuevo
 * Concurso", campos vacíos) y ofrecía un <Select> libre de los 5 estados sin
 * ninguna validación de secuencia. Ahora: el backend decide cuál es el único
 * próximo estado válido (o null si el concurso ya está en su estado final) vía
 * `ObtenerVistaPreviaTransicion`, y esta pantalla solo muestra ese contrato —
 * nombre/año/estado siempre cargados antes de ofrecer ninguna acción, "qué va
 * a pasar" y los hallazgos siempre visibles, nunca colapsados.
 */
export const ConcursoCambiarEstado = () => {
  const { _concursoId } = useParams();
  const [previa, setPrevia] = useState(null);
  const [errorCarga, setErrorCarga] = useState(false);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [transicionConfirmada, setTransicionConfirmada] = useState(false);
  const [recalculando, setRecalculando] = useState(false);
  const sendRequest = useRequestHandler();
  const navigate = useNavigate();

  useEffect(() => {
    setErrorCarga(false);
    sendRequest(
      Servicios.ObtenerVistaPreviaTransicion,
      [_concursoId],
      data => setPrevia(data?.data),
      () => setErrorCarga(true)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_concursoId]);

  const abrirDialogo = () => {
    setTransicionConfirmada(false);
    setDialogoAbierto(true);
  };

  const handleConfirmar = async () => {
    await Servicios.ModificarConcursoEstado(_concursoId, previa.proximoEstado);
    setTransicionConfirmada(true);
  };

  const handleCerrarDialogo = () => {
    setDialogoAbierto(false);
    if (transicionConfirmada) {
      navigate("/concursos");
    }
  };

  // M1-114 (DP-31 a): corregir un resultado mal cargado. Con el concurso en
  // "Resultados disponibles", después de corregir el puntaje en "Cargar
  // resultados", este botón recalcula la penca. Vale en prod: el backend
  // exige estado 5 y resultados íntegros, no manda push y deja registro
  // (RecalculoPencaRegistro). Runbook: docs/runbook-corregir-resultados.md.
  const handleRecalcular = async () => {
    if (
      !window.confirm(
        "Vas a recalcular los puntajes de TODAS las pencas de este concurso con los resultados cargados ahora.\n\n" +
          "No se manda ninguna notificación. Queda registrado quién y cuándo.\n\n" +
          "¿Anotaste el punto de restore y corregiste el resultado? (runbook-corregir-resultados)"
      )
    ) {
      return;
    }
    setRecalculando(true);
    await sendRequest(
      Servicios.RecalcularResultadosPenca,
      [_concursoId],
      data => {
        setRecalculando(false);
        const r = data?.data ?? {};
        window.alert(
          `Penca recalculada. Cambiaron ${r.filasCambiadas ?? "?"} puntajes en ${r.pencasAfectadas ?? "?"} pencas (registro ${r.registroId ?? "?"}).`
        );
      },
      () => {
        setRecalculando(false);
        // M1-114 (revisión H-1/H-5): el motivo lo muestra el snackbar (422).
        // Si la respuesta no llegó, el recálculo pudo haber terminado igual.
        window.alert(
          "El recálculo no se confirmó. No reintentes: revisá el registro de recálculos (RecalculoPencaRegistro) y el motivo que aparece en pantalla antes de volver a intentar."
        );
      }
    );
  };

  if (errorCarga) {
    return (
      <div className="card-modern">
        <div className="page-header">
          <h4 className="page-title">Cambiar estado del concurso</h4>
        </div>
        <hr className="border-gray-200 dark:border-gray-700 my-4" />
        <p>No se pudo cargar el concurso.</p>
      </div>
    );
  }

  if (!previa) {
    return (
      <div className="card-modern">
        <Loading />
      </div>
    );
  }

  const hayProximoEstado = previa.proximoEstado != null;
  const motivoDeshabilitado = !previa.puedeConfirmar
    ? !hayProximoEstado
      ? "El concurso ya está en su estado final."
      : previa.cantidadErrores > 0
      ? "Hay errores que bloquean la transición. Resolvelos para poder continuar."
      : "No se puede confirmar la transición todavía."
    : "";

  return (
    <>
      <div className="card-modern">
        <div className="page-header">
          {/* `previa.nombre` ya incluye el año ("Carnaval 2026"), mismo
              formato que usa el listado de Concursos — agregar `previa.anio`
              de nuevo duplicaba el año en el título ("Carnaval 2026 2026"). */}
          <h4 className="page-title">{previa.nombre ?? ""}</h4>
        </div>
        <hr className="border-gray-200 dark:border-gray-700 my-4" />

        <div className="mb-3 m-2">
          <h5 className="text-center">Cambiar estado del concurso</h5>
          <hr className="border-gray-200 dark:border-gray-700 my-4" />
          <p>
            Estado actual: <strong>{previa.estadoActualDescripcion}</strong>
          </p>
          {hayProximoEstado ? (
            <p>
              Próximo estado: <strong>{previa.proximoEstadoDescripcion}</strong>
            </p>
          ) : (
            <p>
              Este concurso ya se encuentra en su estado final (
              {previa.estadoActualDescripcion}). No hay ninguna transición
              disponible.
            </p>
          )}
          {!hayProximoEstado && previa.estadoActual === 5 && (
              <div className="text-center mt-3">
                <Button
                  variant="outlined"
                  color="error"
                  disabled={recalculando}
                  onClick={handleRecalcular}
                >
                  Recalcular resultados de penca
                </Button>
                <p className="text-sm mt-1">
                  Corrección de resultados: después de corregir un puntaje en
                  "Cargar resultados", recalcula los puntajes de las pencas de
                  este concurso sin cambiar su estado ni mandar notificaciones.
                  Queda registrado. Seguir el runbook de corrección.
                </p>
              </div>
            )}
        </div>

        <hr className="border-gray-200 dark:border-gray-700 my-4" />

        <div className="mb-3 m-2">
          <h5>Qué va a pasar</h5>
          {previa.acciones?.length > 0 ? (
            <ul>
              {previa.acciones.map((accion, i) => (
                <li key={i}>{accion}</li>
              ))}
            </ul>
          ) : (
            <p>Esta transición no dispara ninguna acción adicional.</p>
          )}
        </div>

        <hr className="border-gray-200 dark:border-gray-700 my-4" />

        <div className="mb-3 m-2">
          <HallazgosValidacionList
            hallazgos={previa.hallazgos}
            cantidadErrores={previa.cantidadErrores}
            cantidadAdvertencias={previa.cantidadAdvertencias}
            mensajeVacio="No se encontraron problemas para esta transición."
          />
        </div>

        <hr className="border-gray-200 dark:border-gray-700 my-4" />
        <div className="flex gap-4 justify-center w-auto">
          <div className="flex-1 text-start">
            <Button
              variant={"outlined"}
              onClick={() => navigate("/concursos")}
            >
              Volver
            </Button>
          </div>
          {hayProximoEstado && (
            <div className="flex-1 text-end">
              <Tooltip title={motivoDeshabilitado}>
                <span>
                  <Button
                    variant={"contained"}
                    disabled={!previa.puedeConfirmar}
                    onClick={abrirDialogo}
                  >
                    {`Avanzar a ${previa.proximoEstadoDescripcion}`}
                  </Button>
                </span>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      {hayProximoEstado && (
        <TransicionEstadoDialog
          open={dialogoAbierto}
          onClose={handleCerrarDialogo}
          titulo={`Avanzar a ${previa.proximoEstadoDescripcion}`}
          acciones={previa.acciones}
          hallazgos={previa.hallazgos}
          cantidadErrores={previa.cantidadErrores}
          cantidadAdvertencias={previa.cantidadAdvertencias}
          puedeConfirmar={previa.puedeConfirmar}
          requiereConfirmacionReforzada={previa.requiereConfirmacionReforzada}
          mensajeExito={`Se avanzó a ${previa.proximoEstadoDescripcion} correctamente.`}
          onConfirmar={handleConfirmar}
        />
      )}
    </>
  );
};

export default withAuthenticationRequired(ConcursoCambiarEstado, {
  onRedirecting: () => <Loading />
});
